/**
 * Crease derivation — ADR-028 E3's orchestrator (D2: derive at bake).
 *
 * Given a mesh POSED at a sample angle, this measures what linear blend
 * skinning got wrong there and returns the rest-space displacements a morph
 * target must hold to fix it:
 *
 *     posed  = LBS(bind)                  — what the skin actually does
 *     target = mush(posed)                — what it should have done
 *     D      = target − posed             — the correction, in posed space
 *     d      = L_v⁻¹ · D                  — the same correction, at bind
 *
 * Three things this door insists on, each of which would otherwise produce a
 * plausible and wrong crease:
 *
 * 1. **The mesh must be posed WITH its correctives already driven.** E3
 *    corrects what is left after E1's half-rotation and E2's leaves have had
 *    their say. Derive against an un-augmented bend and the morph re-fixes
 *    what the helper already fixed — a double correction that looks like an
 *    over-inflated elbow and is very hard to attribute. The caller poses; this
 *    door cannot check it, so it is stated loudly here and pinned by the
 *    orchestrating probe.
 * 2. **The band is measured on BIND positions**, never posed ones (the
 *    elbow-evidence law): which flesh belongs to the crease is a property of
 *    the character, not of the frame it happens to be in.
 * 3. **A threshold, not everything.** Mush differs from LBS a little
 *    everywhere; a crease is a local event. Vertices under the threshold are
 *    dropped, which is what keeps the target sparse — the payload question
 *    D5 Q1 asks about, decided here at source rather than by compression.
 *
 * Depends on: three, kernel, delta-mush, rest-space-delta. Depended on by:
 * the bake/graft pipeline and the crease probe.
 */
import { Vector3, type SkinnedMesh } from 'three';
import { mushPosed, type DeltaMushRest } from './delta-mush.js';
import { softenCorrection, type SoftenReport } from './soften-correction.js';
import { median } from './spike-metrics.js';
import { restSpaceDelta } from './rest-space-delta.js';
import type { CreaseDelta } from './graft-crease-morph.js';

export class CreaseDerivationError extends Error {}

/**
 * A vertex's BIND position with the mesh's active morphs already applied.
 *
 * The bind mesh a crease is derived against is not always the base attribute:
 * an imported character can carry a body-shape morph at a nonzero influence —
 * restored by a clip, or simply the way the character is defined — and that
 * displacement IS part of the body being corrected. Skinning the raw base
 * instead derives the crease for a neutral mesh that the viewer never sees,
 * and the correction then stacks wrongly on the real one (Codex P2, #113).
 *
 * Both halves must agree: the rest capture and the posed skinning read through
 * here, or the mush graph describes one body and the LBS baseline another.
 */
export function morphedBindPosition(mesh: SkinnedMesh, vertex: number, target = new Vector3()): Vector3 {
  const position = mesh.geometry.getAttribute('position');
  if (position === undefined) throw new CreaseDerivationError('the mesh has no position attribute');
  target.fromBufferAttribute(position as never, vertex);
  const targets = mesh.geometry.morphAttributes['position'];
  const influences = mesh.morphTargetInfluences;
  if (targets === undefined || influences === undefined || targets.length === 0) return target;
  const relative = mesh.geometry.morphTargetsRelative;
  const baseX = target.x;
  const baseY = target.y;
  const baseZ = target.z;
  for (let i = 0; i < targets.length; i += 1) {
    const weight = influences[i] ?? 0;
    if (weight === 0) continue;
    const attribute = targets[i];
    if (attribute === undefined) continue;
    if (relative) {
      target.x += attribute.getX(vertex) * weight;
      target.y += attribute.getY(vertex) * weight;
      target.z += attribute.getZ(vertex) * weight;
    } else {
      target.x += (attribute.getX(vertex) - baseX) * weight;
      target.y += (attribute.getY(vertex) - baseY) * weight;
      target.z += (attribute.getZ(vertex) - baseZ) * weight;
    }
  }
  return target;
}

/**
 * Default Tikhonov damping. Chosen against the amari elbow: enough to stop the
 * near-singular blow-up at the crease, small enough that a well-conditioned
 * vertex is corrected essentially as the reference asks.
 */
/**
 * Tikhonov damping, default OFF, and the reason is a lesson rather than a
 * preference.
 *
 * The exact inverse amplifies at the crease (144 mm deltas on amari), which
 * looked alarming, so damping and a magnitude clamp were added. Measured, both
 * made things WORSE where the pipeline had been working: on the synthetic
 * elbow, recovery fell from 0.90 to NEGATIVE — the joint ring ended thinner
 * than the collapse it was meant to fix. The amplification is real; the harm
 * from it was assumed, not measured, and the "fix" destroyed a result that was.
 *
 * So the default is the exact solve the round-trip test proves, and whether a
 * large delta actually misbehaves ACROSS the ramp (its only real risk — a
 * morph is applied over a range of angles, not at the one it was derived at)
 * is an OPEN question for the probe to answer with numbers before anything is
 * damped again.
 */
export const DEFAULT_DAMPING = 0;

/** Smoothing passes over the delta field. See `deltaSmoothing`. */
export const DEFAULT_DELTA_SMOOTHING = 3;

export interface CreaseDerivationOptions {
  /**
   * The joint in WORLD bind space (the rig descriptor's `worldBind.position`),
   * carried into the mesh's own bind space here through `bindMatrixInverse`.
   * The caller passes plain numbers on purpose: the Document run is
   * engine-free by the layer law and cannot build a vector.
   */
  readonly jointWorldBind: { readonly x: number; readonly y: number; readonly z: number };
  /**
   * The chain's END joint in the same space. With the joint it gives the limb
   * axis, and the band is a SLAB along that axis — not a sphere around the
   * joint.
   *
   * That distinction is not pedantry, it is the difference between working and
   * finding nothing: E1's crease band is axial (±bandWorld along the limb), and
   * Tripo seats limb joints roughly 2 cm ANTERIOR of the flesh they belong to.
   * Measured on amari, whose band half-width is 17.8 mm: a sphere of that
   * radius centred on the elbow joint contains ZERO of the 11541 vertices, so
   * every crease derived empty. The slab catches the flesh the band actually
   * reweights, which is the flesh a crease is about.
   */
  readonly endWorldBind: { readonly x: number; readonly y: number; readonly z: number };
  /**
   * The bones that count as THIS limb's chain — driver, parent, and their
   * twist ladders (E1's `isChain`). A vertex must carry at least half its
   * weight on them to be part of the crease.
   *
   * Without this the band is a PLANE, infinite sideways, and it sweeps up
   * whatever else lies at the elbow's height: measured on amari, 454 "band"
   * vertices included torso and far-arm flesh, and the derivation produced
   * deltas of 144 mm on a 1 m character — corrections computed for flesh that
   * does not bend here at all. The same infinite-plane trap the wrist station
   * hit (#111).
   */
  readonly chainBones: readonly string[];
  /** Band half-width in world units (ResolvedHinge.bandWorld). */
  readonly bandWorld: number;
  /**
   * Smallest rest-space displacement worth carrying, in world units. Below
   * this a vertex is left alone: mush differs from LBS a little everywhere,
   * and shipping that everywhere would make a full-mesh target out of a local
   * event.
   */
  readonly minDelta: number;
  /**
   * Tikhonov damping for the rest-space solve (0 = the exact inverse).
   *
   * Non-zero by default because the exact inverse amplifies precisely where
   * the crease lives: measured on amari, an undamped solve asked for 144 mm
   * deltas on a 1 m character and a hard magnitude guard then DROPPED 47 of 65
   * band vertices — 90% of the crease lost to keep the rest sane. Damping
   * keeps those vertices with a bounded, if smaller, correction.
   */
  readonly damping?: number;
  /**
   * Largest rest-space delta allowed, direction preserved (default: no cap).
   *
   * Clamping is available and OFF, for the same measured reason as damping.
   * When it was capped at the band, the vertices it caught were the ones AT
   * the joint — they ARE the crease — and the first version DROPPED them
   * instead of clamping, which left the joint ring moving by exactly zero
   * while the outer rings corrected. Both counts are reported either way.
   */
  readonly maxDelta?: number;
  /**
   * Laplacian smoothing passes over the DELTA FIELD before it is emitted
   * (0 disables).
   *
   * Every vertex solves its own correction through its own near-singular
   * inverse, so neighbours disagree — measured on Kara at 75°, a median delta
   * of 16.7 mm with a p99 of 21.5 mm across a 21.9 mm band, i.e. ~30% variation
   * between adjacent flesh. That is not one bad vertex, it is a BUMPY FIELD,
   * and it is what Allen saw in the viewport as "tiny spiking in the crease on
   * deep bends" (field read, Kara, 2026-08-26).
   *
   * Smoothing the field removes the disagreement while keeping the correction.
   *
   * It does NOT taper the band's edge, and this comment once claimed it did.
   * The pass averages each node only against neighbours that also carry a
   * correction — an outside neighbour is never a vote for zero — because
   * averaging into the zero field HALVES the correction (measured: Sergio
   * 0.954 → 0.475). The band's edge is `soften`'s job, below.
   */
  readonly deltaSmoothing?: number;
  /**
   * Feather the MORPH'S OWN CONTRIBUTION past the band and press down what
   * that raises (soften-correction.ts) — a second pass over the derived
   * deltas, applied at the sample pose. Absent = the morph keeps its hard
   * edge and its spikes, which is what every character derived before
   * ADR-036's field read.
   *
   * `deltaSmoothing`'s own doc comment above once claimed it "gives the band a
   * natural EDGE ... the average tapers into" the zeros outside. It does not,
   * and deliberately: averaging into the zero field outside HALVES the
   * correction (measured — Sergio 0.954 -> 0.475), so the pass explicitly
   * refuses to count an outside neighbour as a vote for zero. The taper has to
   * come from pushing the field OUTWARD instead of dragging the inside toward
   * zero, and that is what this does.
   */
  readonly soften?: {
    /**
     * The hinge's bend axis in WORLD space — `profile.hingeAxisLocal` rotated
     * by the driver bone's `worldBind` rotation. Crossed with the limb axis it
     * gives the OUTSIDE of the joint, which is the only half the spike repair
     * is allowed to press on: the inside of a bend is supposed to fold.
     */
    readonly hingeAxisWorld: { readonly x: number; readonly y: number; readonly z: number };
    /** Rings the correction feathers outward (default 2). */
    readonly rings?: number;
    /** Spike-repair passes (default 3). */
    readonly passes?: number;
  };
}

export interface CreaseDerivation {
  readonly deltas: readonly CreaseDelta[];
  /** Vertices inside the band — the candidates. */
  readonly verticesInBand: number;
  /** Of those, the ones that cleared the threshold. */
  readonly verticesKept: number;
  readonly largestDelta: number;
  /** What the softening passes did, or null when none was asked for. */
  readonly soften: SoftenReport | null;
  /** Vertices the inversion refused (unweighted or collapsed), skipped and COUNTED. */
  readonly verticesRefused: number;
  /** Nodes whose frame collapsed at pose and kept their smoothed position (see MushResult). */
  readonly framesLost: number;
  /**
   * Vertices whose rest-space delta exceeded the band and was CLAMPED to it —
   * the inverse amplifying on a near-singular blend. They keep their direction
   * and are still shipped; the count is how much of the crease is being asked
   * for more than it is allowed.
   */
  readonly verticesAmplified: number;
}

const _bind = new Vector3();
const _posed = new Vector3();
const _correction = new Vector3();
const _joint = new Vector3();
const _axis = new Vector3();

/**
 * Derive one crease target's deltas from a mesh already posed at the sample
 * angle, with its correctives driven.
 *
 * `restDetail` must have been captured from this mesh's BIND positions
 * (captureRestDetail), which are also what the returned deltas displace.
 */
export function deriveCreaseDeltas(
  mesh: SkinnedMesh,
  restDetail: DeltaMushRest,
  options: CreaseDerivationOptions,
): CreaseDerivation {
  const { jointWorldBind, endWorldBind, bandWorld, minDelta, chainBones, damping = DEFAULT_DAMPING, maxDelta = Infinity, deltaSmoothing = DEFAULT_DELTA_SMOOTHING, soften } = options;
  if (!(bandWorld > 0)) throw new CreaseDerivationError(`bandWorld must be positive, got ${bandWorld}`);
  if (!(minDelta >= 0)) throw new CreaseDerivationError(`minDelta must be non-negative, got ${minDelta}`);
  const position = mesh.geometry.getAttribute('position');
  if (position === undefined) throw new CreaseDerivationError('the mesh has no position attribute');
  const skinIndex = mesh.geometry.getAttribute('skinIndex');
  const skinWeight = mesh.geometry.getAttribute('skinWeight');
  if (skinIndex === undefined || skinWeight === undefined) {
    throw new CreaseDerivationError('the mesh carries no skinIndex/skinWeight, so no vertex can be placed on a limb');
  }
  const chain = new Set(chainBones);
  const chainSlots = new Set(
    mesh.skeleton.bones.map((b, i) => (chain.has(b.name) ? i : -1)).filter((i) => i >= 0),
  );
  const count = position.count;
  if (restDetail.offsets.length !== count * 3) {
    throw new CreaseDerivationError(
      `the captured rest detail holds ${restDetail.offsets.length / 3} vertices, the mesh has ${count} — ` +
        `it was captured from a different mesh`,
    );
  }

  // The band is asked in the MESH's bind space, so both joints come in from
  // world through the mesh's own bind matrix rather than being assumed to
  // share a frame with the geometry. The axis is built from the two carried
  // points, which keeps it correct under any scale the bind matrix holds.
  _joint.set(jointWorldBind.x, jointWorldBind.y, jointWorldBind.z).applyMatrix4(mesh.bindMatrixInverse);
  _axis.set(endWorldBind.x, endWorldBind.y, endWorldBind.z).applyMatrix4(mesh.bindMatrixInverse).sub(_joint);
  if (_axis.lengthSq() < 1e-16) {
    throw new CreaseDerivationError('the joint and the chain end coincide, so the limb has no axis to band along');
  }
  // The band and threshold arrive in WORLD units but every comparison below is
  // in the mesh's own bind space, so a bind matrix carrying scale would make
  // the band the wrong size — measured on a 0.37-scaled hierarchy it would
  // select nearly three times too much flesh (Sol, review of #113). The ratio
  // of the limb's two lengths IS the scale, and needs no matrix decomposition.
  const limbLocal = _axis.length();
  const limbWorld = Math.hypot(
    endWorldBind.x - jointWorldBind.x,
    endWorldBind.y - jointWorldBind.y,
    endWorldBind.z - jointWorldBind.z,
  );
  const toLocal = limbWorld > 0 ? limbLocal / limbWorld : 1;
  const bandLocal = bandWorld * toLocal;
  const minDeltaLocal = minDelta * toLocal;
  _axis.normalize();

  // What the skin actually does at this pose. applyBoneTransform reads the
  // bones' CURRENT world matrices, so this is the posed shape including
  // whatever E1/E2 are contributing.
  const posed = new Float32Array(count * 3);
  // The softening measures every excess against the surface the vertex sat in
  // AT BIND, so the bind positions are packed in the same walk rather than
  // re-derived in a second one. Allocated only when it is asked for.
  const restPacked = soften === undefined ? null : new Float32Array(count * 3);
  for (let v = 0; v < count; v += 1) {
    // applyBoneTransform skins whatever the TARGET already holds, so seeding it
    // with the morphed bind is what carries an active body-shape morph into
    // the pose the crease is derived against.
    morphedBindPosition(mesh, v, _bind);
    if (restPacked !== null) {
      restPacked[v * 3] = _bind.x;
      restPacked[v * 3 + 1] = _bind.y;
      restPacked[v * 3 + 2] = _bind.z;
    }
    mesh.applyBoneTransform(v, _posed.copy(_bind));
    posed[v * 3] = _posed.x;
    posed[v * 3 + 1] = _posed.y;
    posed[v * 3 + 2] = _posed.z;
  }
  // What it should have done.
  const mush = mushPosed(restDetail, posed);
  const target = mush.positions;
  let softenReport: SoftenReport | null = null;

  // ── THE SOFTENING REGIONS, exactly as the slender-elbow study defined them ──
  // Computed here, USED after the morph is derived (see the second pass below).
  // Three nested slabs along the limb, in the mesh's BIND space, limb flesh only:
  //
  //   allowed   band x 3      how far the feather may reach
  //   selected  band x 1.75   what the spike metrics are measured over
  //   outer     selected, on the OUTSIDE of the bend — the only half the
  //             repair may press, because the inside is meant to fold
  //
  // Wider than the band the deltas are emitted for: that is the point. The
  // correction is measured in the band and feathered past it.
  let softenRegions: { allowed: Set<number>; selected: Set<number>; outer: Set<number>; radius: number } | null = null;
  if (soften !== undefined && restPacked !== null) {
    const allowed = new Set<number>();
    const selected = new Set<number>();
    const outer = new Set<number>();
    const _r = new Vector3();
    const centre = new Vector3();
    for (let v = 0; v < count; v += 1) {
      let chainWeight = 0;
      for (let k = 0; k < skinWeight.itemSize; k += 1) {
        if (chainSlots.has(skinIndex.getComponent(v, k))) chainWeight += skinWeight.getComponent(v, k);
      }
      if (chainWeight < 0.5) continue;
      _r.set(restPacked[v * 3]!, restPacked[v * 3 + 1]!, restPacked[v * 3 + 2]!).sub(_joint);
      const axial = Math.abs(_r.dot(_axis));
      if (axial < bandLocal * 3) allowed.add(v);
      if (axial < bandLocal * 1.75) {
        selected.add(v);
        centre.add(_r);
      }
    }
    if (selected.size > 0) {
      // THE ONE DEVIATION FROM THE STUDY, named. It centred the outside/inside
      // split on the elbow-section's measured cross-section centroid, which
      // this door does not receive. The band's own centroid is used instead —
      // the same quantity the section measures, taken over the slab rather
      // than one slice. It matters because the joint is NOT the centre: Tripo
      // seats limb joints ~2 cm anterior of the flesh (the Egyptian finding),
      // so splitting on the joint would call a third of the outside "inside".
      centre.multiplyScalar(1 / selected.size).add(_joint);
      const anterior = new Vector3(soften.hingeAxisWorld.x, soften.hingeAxisWorld.y, soften.hingeAxisWorld.z)
        .transformDirection(mesh.bindMatrixInverse)
        .cross(_axis);
      if (anterior.lengthSq() > 1e-16) {
        anterior.normalize();
        const radii: number[] = [];
        for (const v of selected) {
          _r.set(restPacked[v * 3]!, restPacked[v * 3 + 1]!, restPacked[v * 3 + 2]!).sub(centre);
          if (_r.dot(anterior) < 0) outer.add(v);
          radii.push(_r.addScaledVector(_axis, -_r.dot(_axis)).length());
        }
        softenRegions = { allowed, selected, outer, radius: median(radii) };
      }
    }
  }

  const deltas: CreaseDelta[] = [];
  let verticesInBand = 0;
  let largestDelta = 0;
  // Sets, not counters: the second pass meets the same vertices again, and a
  // hole the feather later fills is no longer a hole (Codex P2 x2, #167).
  const refused = new Set<number>();
  const amplified = new Set<number>();
  /** Every vertex the band accepted — the smoothing pass re-collects over these. */
  const banded: number[] = [];
  const bandSet = new Set<number>();
  for (let v = 0; v < count; v += 1) {
    // The band is a BIND-space question: which flesh belongs to the crease is
    // a property of the character, not of the frame it is measured in.
    morphedBindPosition(mesh, v, _bind);
    if (Math.abs(_bind.sub(_joint).dot(_axis)) > bandLocal) continue;
    // ...and it must be THIS limb's flesh. Half, not a sliver: a torso vertex
    // holding 10% of an arm bone still behaves like torso, and deriving a
    // crease for it corrects a bend it never makes.
    let chainWeight = 0;
    for (let k = 0; k < skinWeight.itemSize; k += 1) {
      if (chainSlots.has(skinIndex.getComponent(v, k))) chainWeight += skinWeight.getComponent(v, k);
    }
    if (chainWeight < 0.5) continue;
    verticesInBand += 1;
    banded.push(v);
    bandSet.add(v);

    _correction.set(target[v * 3]! - posed[v * 3]!, target[v * 3 + 1]! - posed[v * 3 + 1]!, target[v * 3 + 2]! - posed[v * 3 + 2]!);
    let rest: Vector3;
    try {
      rest = restSpaceDelta(mesh, v, _correction, new Vector3(), damping);
    } catch {
      // An unweighted or collapsed vertex names no correction. Skipped and
      // COUNTED — a silent skip here would quietly thin the crease.
      refused.add(v);
      continue;
    }
    const magnitude = rest.length();
    if (magnitude < minDeltaLocal) continue;
    // AMPLIFICATION GUARD — a CLAMP, not a drop, and the difference is the
    // whole crease.
    //
    // Where skinning collapses hardest the blend is nearly singular, so the
    // inverse amplifies: measured on amari at 135°, a few millimetres of posed
    // correction asked for a 144 mm rest-space delta. Such a delta is right at
    // the sample angle and wild at every other angle the ramp drives it
    // through, so it cannot ship as-is.
    //
    // The first version DROPPED those vertices, and measuring showed why that
    // is the worst available choice: the near-singular vertices are the ones
    // AT THE JOINT — they ARE the crease. Dropping them kept the outer rings
    // and deleted the middle, so the joint ring moved not at all (measured on
    // the synthetic elbow: the applied morph changed the joint girth by
    // exactly zero, while 39 of 91 band vertices were discarded).
    //
    // Clamping keeps every vertex and bounds the ask: direction preserved,
    // magnitude capped at the band. Those vertices get LESS correction than
    // the reference wants, which is a shortfall the probe can measure, rather
    // than a hole nothing can.
    if (magnitude > maxDelta) {
      rest.multiplyScalar(maxDelta / magnitude);
      amplified.add(v);
    }
    largestDelta = Math.max(largestDelta, rest.length());
    deltas.push({ vertex: v, dx: rest.x, dy: rest.y, dz: rest.z });
  }
  if (deltaSmoothing > 0 && deltas.length > 0) {
    // Smooth the field WITHIN THE BAND ONLY — each node averaged against its
    // neighbours that also carry a correction, never against the zeros
    // outside.
    //
    // Smoothing across the whole mesh was tried first and measured: it halves
    // the spread AND halves the correction (Sergio's 75° recovery fell 0.954 →
    // 0.475, Hamus 0.654 → 0.310), because averaging into the zero field
    // outside the band drags every delta toward zero. Restricting it keeps the
    // magnitude and removes only the disagreement between neighbours, which is
    // the actual defect: the spikes are neighbours solving independently
    // through their own near-singular inverses, not a field that is too strong.
    const graph = restDetail.graph;
    let field = new Map<number, [number, number, number]>();
    for (const d of deltas) field.set(graph.weldOf[d.vertex]!, [d.dx, d.dy, d.dz]);
    for (let pass = 0; pass < deltaSmoothing; pass += 1) {
      const next = new Map<number, [number, number, number]>();
      for (const [node, current] of field) {
        let sx = 0;
        let sy = 0;
        let sz = 0;
        let n = 0;
        for (const m of graph.neighbors[node] ?? []) {
          const neighbour = field.get(m);
          if (neighbour === undefined) continue; // outside the band: not a vote for zero
          sx += neighbour[0];
          sy += neighbour[1];
          sz += neighbour[2];
          n += 1;
        }
        if (n === 0) {
          next.set(node, current);
          continue;
        }
        next.set(node, [
          current[0] + 0.5 * (sx / n - current[0]),
          current[1] + 0.5 * (sy / n - current[1]),
          current[2] + 0.5 * (sz / n - current[2]),
        ]);
      }
      field = next;
    }
    deltas.length = 0;
    largestDelta = 0;
    for (const v of banded) {
      const evened = field.get(graph.weldOf[v]!);
      if (evened === undefined) continue;
      const magnitude = Math.hypot(evened[0], evened[1], evened[2]);
      if (magnitude < minDeltaLocal) continue;
      largestDelta = Math.max(largestDelta, magnitude);
      deltas.push({ vertex: v, dx: evened[0], dy: evened[1], dz: evened[2] });
    }
  }
  if (softenRegions !== null && restPacked !== null && deltas.length > 0 && soften !== undefined) {
    // ── THE SECOND PASS: soften the MORPH, not the reference (2026-09-08) ──
    //
    // Measured on amari / goblin / gilda / kobold, θ 60–135: the posed mesh
    // WITHOUT the crease morph has ~0 spike candidates; WITH it, 13–16 on
    // amari at 75°, tracking the ramp weight exactly and vanishing where it is
    // zero. THE SPIKES ARE THE MORPH'S OWN FIELD — the "tiny spiking in the
    // crease" the deltaSmoothing comment above records, still there at 3
    // passes. Astra's study healed them by feathering the morph's APPLIED
    // contribution (their `base` = capture with the morph zeroed, `current` =
    // with it): every visible spike gone (none >= 1 edge length) on all four.
    // Feathering the mush reference instead — the first wiring — changed
    // nothing, because the reference has no spikes to begin with.
    //
    // So: apply the deltas just derived through each vertex's skinning to get
    // the morph's posed contribution, run the pair on with-vs-without (a field
    // that is band-confined BY CONSTRUCTION, the study's condition), and fold
    // the feathered contribution back to rest space through the same inverse.
    // The feather is linear in the field, so one morph softened at its sample
    // angle and scaled by the ramp is exact at every angle the ramp drives.
    const deltaOf = new Map<number, CreaseDelta>();
    for (const d of deltas) deltaOf.set(d.vertex, d);
    const posedWith = new Float32Array(count * 3);
    for (let v = 0; v < count; v += 1) {
      morphedBindPosition(mesh, v, _bind);
      const d = deltaOf.get(v);
      if (d !== undefined) _bind.set(_bind.x + d.dx, _bind.y + d.dy, _bind.z + d.dz);
      mesh.applyBoneTransform(v, _posed.copy(_bind));
      posedWith[v * 3] = _posed.x;
      posedWith[v * 3 + 1] = _posed.y;
      posedWith[v * 3 + 2] = _posed.z;
    }
    const softened = softenCorrection(
      restDetail.graph,
      restPacked,
      mesh.geometry.getIndex()?.array ?? null,
      posed,
      posedWith,
      softenRegions,
      soften.rings,
      soften.passes,
    );
    softenReport = softened.report;
    // Fold back — the ADJUSTMENT only. What the two passes changed is
    // `softened − posedWith`; that alone goes through the inverse and is ADDED
    // to the first-pass delta. A vertex the passes left alone keeps its delta
    // verbatim, so damping is applied exactly once: the first wiring re-solved
    // every delta from its skinned contribution, and with damping > 0 that is
    // a lossy round trip — (LᵀL + λI)⁻¹Lᵀ(L·d) ≠ d — which shrank the whole
    // crease merely for enabling softening (Codex P2, #167; the guard test now
    // runs with real damping and pins it). The inverse still refuses on a
    // singular matrix; an untouched delta survives that refusal unchanged.
    largestDelta = 0;
    const folded: CreaseDelta[] = [];
    for (const v of softenRegions.allowed) {
      _correction.set(
        softened.positions[v * 3]! - posedWith[v * 3]!,
        softened.positions[v * 3 + 1]! - posedWith[v * 3 + 1]!,
        softened.positions[v * 3 + 2]! - posedWith[v * 3 + 2]!,
      );
      const original = deltaOf.get(v);
      if (_correction.lengthSq() < 1e-24) {
        // Untouched by both passes — exact copies come back exactly.
        if (original !== undefined) {
          folded.push(original);
          largestDelta = Math.max(largestDelta, Math.hypot(original.dx, original.dy, original.dz));
        }
        continue;
      }
      let adjust: Vector3;
      try {
        adjust = restSpaceDelta(mesh, v, _correction, new Vector3(), damping);
      } catch {
        refused.add(v);
        if (original !== undefined) {
          folded.push(original);
          largestDelta = Math.max(largestDelta, Math.hypot(original.dx, original.dy, original.dz));
        }
        continue;
      }
      const rest = original === undefined ? adjust : adjust.add(new Vector3(original.dx, original.dy, original.dz));
      const magnitude = rest.length();
      if (magnitude < minDeltaLocal) continue;
      if (magnitude > maxDelta) {
        rest.multiplyScalar(maxDelta / magnitude);
        amplified.add(v);
      }
      // A first-pass hole the feather reached and the inverse accepted is filled.
      refused.delete(v);
      largestDelta = Math.max(largestDelta, rest.length());
      folded.push({ vertex: v, dx: rest.x, dy: rest.y, dz: rest.z });
    }
    deltas.length = 0;
    deltas.push(...folded);
  }

  return {
    deltas,
    verticesInBand,
    // Band-scoped, per its contract ("of those, the ones that cleared the
    // threshold"): after the second pass `deltas` also holds the feathered
    // vertices OUTSIDE the band, which `soften.extended` reports (Codex P2, #167).
    verticesKept: deltas.filter((d) => bandSet.has(d.vertex)).length,
    largestDelta,
    verticesRefused: refused.size,
    soften: softenReport,
    verticesAmplified: amplified.size,
    framesLost: mush.framesLost,
  };
}
